import { useQuery, useQueries } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaEmailRecordsV1Service } from '../service';
import { GetDomainsByOrgIDIO } from '../types';

export const getGetDomainsByOrgIDQueryKey = (req: GetDomainsByOrgIDIO['input']) =>
  ['EmailRecordsV1', 'GetDomainsByOrgID', req.orgId] as const;
type QueryKey = ReturnType<typeof getGetDomainsByOrgIDQueryKey>;

/**
 * A hook that returns a query for the `GetDomainsByOrgID` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetDomainsByOrgIDQuery = <E = unknown, D = GetDomainsByOrgIDIO['output']>(
  req: GetDomainsByOrgIDIO['input'],
  options?: LimitedSchemaQueryOptions<GetDomainsByOrgIDIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetDomainsByOrgIDIO['output'], E, D, QueryKey>({
    queryKey: getGetDomainsByOrgIDQueryKey(req),
    queryFn: () => SchemaEmailRecordsV1Service.GetDomainsByOrgID(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetDomainsByOrgID` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetDomainsByOrgIDQueries = <E = unknown, D = GetDomainsByOrgIDIO['output']>(
  argsArr: {
    req: GetDomainsByOrgIDIO['input'];
    options?: LimitedSchemaQueryOptions<GetDomainsByOrgIDIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetDomainsByOrgIDQueryKey(req),
      queryFn: () => SchemaEmailRecordsV1Service.GetDomainsByOrgID(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetDomainsByOrgIDQuery<E, D>>[];
